<template>
<div class="section__wrapper">
  <div class="section__inner">
    <!-- Запасной заголовок зашит по-русски: над «не найдена» он встал бы
      и под английским сообщением. В шапке и так «Сессия» из локали. -->
    <h1 v-if="!isNotFound">{{ sectionTitle }}</h1>
    <div
      v-if="isNotFound"
      class="section-card"
    >
      <p>{{ $t('sectionPage.not_found') }}</p>
    </div>
    <div
      v-if="isMeta"
      class="section-card"
    >
      <ul class="section-meta no-dot">
        <li v-if="sectionDate">
          <i class="exu-calendar-date" />
          <span>{{ sectionDate }}</span>
        </li>
        <li v-if="sectionTime">
          <i class="exu-clock" />
          <span>{{ sectionTime }}</span>
        </li>
        <li v-if="section.place">
          <i class="exu-marker" />
          <span>{{ section.place }}</span>
        </li>
      </ul>
    </div>
    <!-- Заголовок общий для карточек ниже: описания может не быть, а спикеры
      есть, и подзаголовок внутри описания пропал бы. -->
    <h2
      v-if="isAbout"
      class="section-heading"
    >
      {{ $t('sectionPage.about') }}
    </h2>
    <div
      v-if="sectionDescription"
      class="section-card"
    >
      <div
        v-if="descriptionFormat === 'html'"
        v-html="sectionDescription"
        class="section-description"
      />
      <p v-else>{{ sectionDescription }}</p>
    </div>
    <div
      v-if="isLectures"
      class="section-card"
    >
      <h2 class="section-title">Темы лекций</h2>
      <ul>
        <li v-for="lecture of section.lectures"
          :key="lecture.id"
        >
          {{ lecture.title }}
        </li>
      </ul>
    </div>
    <div
      v-if="isModerators || isSpeakers"
      class="section-card"
    >
      <template v-if="isModerators">
        <h3 class="section-title">Модератор</h3>
        <ul class="no-dot">
          <li v-for="moderator of sectionModerators"
            :key="`moder-${moderator.id}`"
          >
            <div class="section-persone__wrapper">
              <div
                class="section-persone__photo"
              >
                <div
                  :class="[!moderator.photo ? photoAlter() : '']"
                  class="persone-photo"
                >
                  <img v-if="moderator.photo" :src="moderator.photo" alt="">
                  <div v-else class="no-photo">
                    <span>{{ noPhoto(moderator) }}</span>
                  </div>
                </div>
              </div>
              <div class="section-persone">
                <div
                  v-if="moderator.name"
                  class="section-persone__name"
                >
                  {{ moderator.name }}
                </div>
                <div
                  v-if="moderator.position"
                  class="section-persone__position"
                >
                  {{ moderator.position }}
                </div>
                <div
                  v-if="moderator.company"
                  class="section-persone__company"
                >
                  {{ moderator.company }}
                </div>
              </div>
            </div>
          </li>
        </ul>
      </template>
      <template v-if="isSpeakers">
        <h3 class="section-title">Спикеры</h3>
        <ul class="speakers-list no-dot">
          <li v-for="speaker of sectionSpeakers"
            :key="`speaker-${speaker.id}`"
          >
            <div class="section-persone__wrapper">
              <div
                class="section-persone__photo"
              >
                <div
                  :class="[!speaker.photo ? photoAlter() : '']"
                  class="persone-photo"
                >
                  <img v-if="speaker.photo" :src="speaker.photo" alt="">
                  <div v-else class="no-photo">
                    <span>{{ noPhoto(speaker) }}</span>
                  </div>
                </div>
              </div>
              <div class="section-persone">
                <div
                  v-if="speaker.name"
                  class="section-persone__name"
                >
                  {{ speaker.name }}
                </div>
                <div
                  v-if="speaker.position"
                  class="section-persone__position"
                >
                  {{ speaker.position }}
                </div>
                <div
                  v-if="speaker.company"
                  class="section-persone__company"
                >
                  {{ speaker.company }}
                </div>
              </div>
            </div>
          </li>
        </ul>
      </template>
    </div>
  </div>
</div>
</template>
<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import { DateTime } from 'luxon';
import appBaseNav from '@/components/mixins/appBaseNav';
import sections from '@/components/mixins/methodsSections';

export default {
  name: 'SectionPageView',
  mixins: [appBaseNav, sections],
  props: {
    section_id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      section: null,
    };
  },
  computed: {
    sectionTitle() {
      return this.section?.title ? `${this.section?.name} ${this.section?.title}`.trim() : (this.section?.name || 'Детали сессии');
    },
    sectionDescription() {
      return this.section?.description;
    },
    // На несуществующую секцию сервер отвечает 200 с пустым results, и
    // getSection отдаёт {}. До ответа section ещё null — это не «не найдена».
    isNotFound() {
      return this.section !== null && !this.section.id;
    },
    descriptionFormat() {
      return this.section?.description_format || 'text';
    },
    // Пустой массив сам по себе истинен, поэтому секция без лекций, модераторов
    // или спикеров выводила заголовок над пустым списком. Считаем по длине.
    // Время считаем так же, как карточка программы: тот же пояс, тот же формат,
    // чтобы страница и список не расходились.
    timeStart() {
      if (!Number.isFinite(this.section?.start_ts)) {
        return null;
      }
      return DateTime.fromMillis(this.section.start_ts)
        .setZone(this.$store.getters.getTime_UTC)
        .setLocale(this.$store.getters.getLang);
    },
    sectionDate() {
      return this.timeStart ? this.timeStart.toFormat('d MMMM') : '';
    },
    sectionTime() {
      if (!this.timeStart) {
        return '';
      }
      const timeEnd = this.timeStart.plus(this.section.duration_ts || 0);
      return `${this.timeStart.toFormat('H:mm')} - ${timeEnd.toFormat('H:mm')}`;
    },
    isMeta() {
      return !!(this.sectionDate || this.section?.place);
    },
    // Есть ли под карточкой времени и места хоть одна карточка о сессии.
    isAbout() {
      return !!this.sectionDescription
        || this.isLectures || this.isModerators || this.isSpeakers;
    },
    isLectures() {
      return this.section?.lectures?.length > 0;
    },
    isModerators() {
      return this.sectionModerators?.length > 0;
    },
    isSpeakers() {
      return this.sectionSpeakers?.length > 0;
    },
    sectionModerators() {
      return this.section?.moderators;
    },
    sectionSpeakers() {
      return this.section?.speakers;
    },
  },
  watch: {
    locale() {
      this.$store.commit('setPageTitle', this.$t('sectionPage.header_title'));
    },
  },
  mounted() {
    this.$store.commit('setPageTitle', this.$t('sectionPage.header_title'));
    this.$emit('loading', true);
    Promise.all([
      this.getSection(this.section_id),
    ])
      .then(([
        section,
      ]) => {
        this.section = { ...section };
        console.log(section);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.$emit('loading', false);
      });
  },
  methods: {
    noPhoto(persone) {
      const [fname, lname] = persone.name.split(' ');
      return `${fname[0]}${lname[0]}`;
    },
    photoAlter() {
      return `alter-photo-${['rose', 'orange', 'blue', 'purpure', 'lilac', 'green'][Math.floor(Math.random() * 6)]}`;
    },
  },
};
</script>
<style lang="scss">
@import '@/assets/style/variables.scss';

.section {
  &__wrapper {
    h1, h2, h3, p, ul, ol {
      margin: 0;
    }
    ul {
      li {
        &::before {
          content: '';
          display: block;
          border-radius: 50%;
          width: 8px;
          height: 8px;
          background-color: $color-accent;
          margin-left: -16px;
          flex: 0 0 auto;
        }
        display: flex;
        align-items: center;
        margin-bottom: 10px;
        gap: 12px;

        &:last-child {
          margin-bottom: 0;
        }
      }
      padding-left: 20px;

      &.no-dot {
        padding-left: 0;
        li {
          &::before {
            display: none;
          }
        }
      }
    }
  }
}
</style>
<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

.section {
  &__wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 100%;
    padding: 0 12px;
    gap: 10px;
  }
  &__inner {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 760px;
    min-height: 100%;
    margin: 20px auto;
    gap: 10px;
  }

  &-card {
    padding: 20px;
    border-radius: 20px;
    background-color: #ffffff;
  }

  &-title {
    margin-bottom: 10px;
  }

  // HTML описания приходит с сервера без классов: абзацы и списки шли впритык.
  // Отступ — только между прямыми соседями и только у элемента без своего
  // style: что редактор задал сам, мы не перебиваем. Стиль scoped, наружу не
  // выходит.
  &-description > :deep(* + *:not([style])) {
    margin-top: 1em;
  }

  // Список модераторов не должен липнуть к заголовку «Спикеры».
  &-card ul + &-title {
    margin-top: 10px;
  }

  // К зазору между карточками (10px) добавляется свой: заголовок должен
  // отходить от карточки времени и места, а не липнуть к ней.
  &-heading {
    margin-top: 10px;
  }

  // Акцентом остались только иконки, текст обычный, как в описании.
  // Иконка у первой строки, а не по центру: зал может уйти на несколько строк,
  // поэтому высота строки задана числом и у текста, и у иконки.
  &-meta {
    line-height: 24px;

    li {
      align-items: flex-start;
    }

    i {
      flex: 0 0 auto;
      font-size: 20px;
      line-height: 24px;
      color: $color-accent;
    }
  }

  &-persone {
    &__wrapper {
      display: flex;
      flex-direction: row;
      gap: 10px;

      .persone-photo {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 50px;
        height: 50px;
        background-color: #ff8844;
        border-radius: 50%;
        margin-top: 6px;
        overflow: hidden;

        &.alter-photo {
          &-rose {
            background: #FF7777;
          }
          &-orange {
            background: #FFAA77;
          }
          &-blue {
            background: #77AAFF;
          }
          &-purpure {
            background: #FF77DD;
          }
          &-lilac {
            background: #8850CC;
          }
          &-green {
            background: #40BB40;
          }
        }
        & > img {
          width: 100%;
        }
        & > .no-photo {
          font-size: 20px;
          line-height: 20px;
          color: #FFFFFF;
        }

      }
    }
    &__name {
      font-weight: 700;
    }
    &__position {
      font-style: italic;
    }
  }

  display: flex;
  flex-direction: column;
  row-gap: 12px;
  padding: 0 12px 12px;
}
.speakers-list {
  li {
    margin-bottom: 10px;
    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
